import { useState, useEffect } from 'react'
import { Navigate, Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Eye, EyeOff, CheckCircle, ArrowRight } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { ROUTES } from '@/constants/routes'
import { authService } from '@/services/authService'

export default function ResetPasswordPage() {
  const { t } = useTranslation()
  const { isAuth } = useAuth()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPass, setShowPass] = useState(false)
  const [showConfirmPass, setShowConfirmPass] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError]       = useState(null)
  const [success, setSuccess]   = useState(false)
  const [mounted, setMounted]   = useState(false)

  useEffect(() => { setMounted(true) }, [])

  if (isAuth) return <Navigate to={ROUTES.DASHBOARD} replace />

  if (!token && !success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Invalid Reset Link</h2>
          <p className="text-gray-400 mb-6">The password reset link is missing or invalid.</p>
          <Link to={ROUTES.LOGIN} className="text-orange-DEFAULT hover:underline">
            Go back to login
          </Link>
        </div>
      </div>
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!password || !confirmPassword) return
    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch', 'Passwords do not match'))
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      await authService.resetPassword(token, password)
      setSuccess(true)
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #000666 0%, #1A237E 50%, #000444 100%)' }}
    >
      {/* Background decorative blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(255,109,0,0.4) 0%, transparent 70%)' }}
        />
        <div
          className="absolute -bottom-60 -left-20 w-[600px] h-[600px] rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, rgba(27,109,36,0.5) 0%, transparent 70%)' }}
        />
        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Card */}
      <div
        className={`relative z-10 w-full max-w-md mx-4 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        {/* Logo section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <span
              className="text-4xl font-black text-white"
              style={{ fontFamily: 'Cairo, sans-serif', letterSpacing: '-0.02em' }}
            >
              Hagtna
            </span>
            <span
              className="w-2 h-2 rounded-full bg-orange-DEFAULT inline-block"
              style={{ background: '#FF6D00' }}
            />
            <span
              className="text-4xl font-black text-white"
              style={{ fontFamily: 'Cairo, sans-serif' }}
            >
              حاجاتنا
            </span>
          </div>
          <div
            className="inline-block px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest"
            style={{
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: 'rgba(255,255,255,0.8)',
              fontFamily: 'Tajawal, sans-serif',
            }}
          >
            {t('app.admin')}
          </div>
        </div>

        {/* Card */}
        <div
          className="rounded-[24px] p-8"
          style={{
            background: 'rgba(255,255,255,0.08)',
            backdropFilter: 'blur(32px)',
            border: '1px solid rgba(255,255,255,0.15)',
            boxShadow: '0 32px 64px rgba(0,0,0,0.3)',
          }}
        >
          <h2
            className="text-2xl font-black text-white mb-1"
            style={{ fontFamily: 'Cairo, sans-serif' }}
          >
            {t('auth.resetPassword', 'Reset Password')}
          </h2>
          <p className="text-white/60 text-sm mb-8" style={{ fontFamily: 'Tajawal, sans-serif' }}>
            {t('auth.resetPasswordSubtitle', 'Enter your new password below')}
          </p>

          {/* Error alert */}
          {error && (
            <div
              className="flex items-center gap-3 p-3 rounded-xl mb-6 animate-in"
              style={{ background: 'rgba(186,26,26,0.15)', border: '1px solid rgba(186,26,26,0.3)' }}
            >
              <AlertCircle size={18} style={{ color: '#FFDAD6', flexShrink: 0 }} />
              <span className="text-sm" style={{ color: '#FFDAD6', fontFamily: 'Tajawal, sans-serif' }}>
                {error}
              </span>
            </div>
          )}
          
          {success ? (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 text-green-400 mb-4">
                <CheckCircle size={32} />
              </div>
              <p className="text-white mb-6" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                {t('auth.resetSuccess', 'Your password has been successfully reset. You can now login with your new password.')}
              </p>
              <Link
                to={ROUTES.LOGIN}
                className="inline-flex items-center justify-center gap-2 text-sm font-bold text-orange-DEFAULT hover:text-orange-400 transition-colors"
                style={{ fontFamily: 'Tajawal, sans-serif', color: '#FF6D00' }}
              >
                {t('auth.loginNow', 'Login Now')} <ArrowRight size={16} className="rtl:rotate-180" />
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Password */}
              <div>
                <label
                  className="block text-sm font-bold mb-2"
                  style={{ color: 'rgba(255,255,255,0.8)', fontFamily: 'Cairo, sans-serif' }}
                >
                  {t('auth.newPassword', 'New Password')}
                </label>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    className="w-full rounded-xl px-4 py-3 pr-12 text-sm outline-none transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: '#fff',
                      fontFamily: 'Tajawal, sans-serif',
                      textAlign: 'right',
                    }}
                    onFocus={(e) => {
                      e.target.style.background = 'rgba(255,255,255,0.15)'
                      e.target.style.border = '1px solid rgba(255,109,0,0.6)'
                    }}
                    onBlur={(e) => {
                      e.target.style.background = 'rgba(255,255,255,0.1)'
                      e.target.style.border = '1px solid rgba(255,255,255,0.2)'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors"
                  >
                    {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label
                  className="block text-sm font-bold mb-2"
                  style={{ color: 'rgba(255,255,255,0.8)', fontFamily: 'Cairo, sans-serif' }}
                >
                  {t('auth.confirmPassword', 'Confirm Password')}
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPass ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    className="w-full rounded-xl px-4 py-3 pr-12 text-sm outline-none transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: '#fff',
                      fontFamily: 'Tajawal, sans-serif',
                      textAlign: 'right',
                    }}
                    onFocus={(e) => {
                      e.target.style.background = 'rgba(255,255,255,0.15)'
                      e.target.style.border = '1px solid rgba(255,109,0,0.6)'
                    }}
                    onBlur={(e) => {
                      e.target.style.background = 'rgba(255,255,255,0.1)'
                      e.target.style.border = '1px solid rgba(255,255,255,0.2)'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPass(!showConfirmPass)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors"
                  >
                    {showConfirmPass ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading || !password || !confirmPassword}
                className="w-full py-3.5 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all mt-2"
                style={{
                  background: isLoading ? 'rgba(255,109,0,0.6)' : '#FF6D00',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '16px',
                  boxShadow: '0 8px 24px rgba(255,109,0,0.35)',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transform: isLoading ? 'none' : undefined,
                }}
              >
                {isLoading ? (
                  <>
                    <span
                      className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                      style={{ animation: 'spin 0.8s linear infinite' }}
                    />
                    {t('auth.resetting', 'Resetting...')}
                  </>
                ) : (
                  t('auth.resetPasswordBtn', 'Reset Password')
                )}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <p
          className="text-center mt-6 text-white/30 text-xs"
          style={{ fontFamily: 'Tajawal, sans-serif' }}
        >
          Hagatna Multi-Vendor Platform — Admin Panel v1.0
        </p>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: rgba(255,255,255,0.35); }
      `}</style>
    </div>
  )
}
